import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import MergeTagPicker from '../components/email-templates/MergeTagPicker'
import TemplatePreviewPane from '../components/email-templates/TemplatePreviewPane'
import PreviewFullscreenModal from '../components/email-templates/PreviewFullscreenModal'

/**
 * EmailDesignerPage
 *
 * Full-page template designer for a specific email type.
 * Replaces the previous EmailTemplateModal overlay with a dedicated page
 * that has breadcrumb navigation and uses the full viewport.
 */
const EmailDesignerPage = () => {
  const { emailTypeKey } = useParams()
  const navigate = useNavigate()

  const [emailTypeLabel, setEmailTypeLabel] = useState('')
  const [templates, setTemplates] = useState([])
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [htmlContent, setHtmlContent] = useState('')
  const [templateName, setTemplateName] = useState('')
  const [description, setDescription] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pageLoading, setPageLoading] = useState(true)

  // Merge tags
  const [mergeTags, setMergeTags] = useState([])
  const [groupedTags, setGroupedTags] = useState({})
  const [filters, setFilters] = useState([])
  const [validationResult, setValidationResult] = useState(null)
  const [showTagPicker, setShowTagPicker] = useState(true)
  const [showPreview, setShowPreview] = useState(true)
  const [requiredContexts, setRequiredContexts] = useState([])
  const [previewExpanded, setPreviewExpanded] = useState(false)
  const [fullscreenOpen, setFullscreenOpen] = useState(false)

  // Track unsaved changes
  const [isDirty, setIsDirty] = useState(false)

  const textareaRef = useRef(null)

  useEffect(() => {
    if (emailTypeKey) {
      loadEmailTypeInfo()
      loadTemplates()
      loadMergeTagCatalog()
    }
  }, [emailTypeKey])

  // Validate content when it changes
  useEffect(() => {
    if (htmlContent) {
      validateTemplate()
    }
  }, [htmlContent])

  const loadEmailTypeInfo = async () => {
    try {
      const response = await fetch('/admin/email_types?active_only=false')
      if (!response.ok) return
      const data = await response.json()
      const emailType = data.find(et => et.key === emailTypeKey)
      if (emailType) {
        setEmailTypeLabel(emailType.name)
      }
    } catch (error) {
      console.error('Error loading email type info:', error)
    }
  }

  const loadTemplates = async () => {
    try {
      setPageLoading(true)
      const response = await fetch(`/admin/email_templates?email_type=${emailTypeKey}`)
      const data = await response.json()
      setTemplates(data)
      const defaultTemplate = data.find(t => t.is_default)
      if (defaultTemplate) {
        loadTemplate(defaultTemplate.id)
      }
    } catch (error) {
      console.error('Error loading templates:', error)
    } finally {
      setPageLoading(false)
    }
  }

  const loadMergeTagCatalog = async () => {
    try {
      const response = await fetch(`/admin/email_templates/merge_tag_catalog?email_type=${emailTypeKey}`)
      const data = await response.json()
      setMergeTags(data.tags || [])
      setGroupedTags(data.grouped_tags || {})
      setFilters(data.filters || [])
      const contexts = [...new Set((data.tags || []).map(t => t.context))]
      setRequiredContexts(contexts)
    } catch (error) {
      console.error('Error loading merge tag catalog:', error)
    }
  }

  const validateTemplate = async () => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch('/admin/email_templates/validate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          html_content: htmlContent,
          email_type: emailTypeKey
        })
      })
      const data = await response.json()
      setValidationResult(data)
    } catch (error) {
      console.error('Error validating template:', error)
    }
  }

  const loadTemplate = async (templateId) => {
    try {
      const response = await fetch(`/admin/email_templates/${templateId}`)
      const data = await response.json()
      setSelectedTemplate(data)
      setHtmlContent(data.html_content)
      setTemplateName(data.name)
      setDescription(data.description || '')
      setIsCreating(false)
      setIsDirty(false)
    } catch (error) {
      console.error('Error loading template:', error)
    }
  }

  const createNewTemplate = () => {
    setSelectedTemplate(null)
    setHtmlContent('')
    setTemplateName('')
    setDescription('')
    setIsCreating(true)
    setIsDirty(false)
  }

  const saveTemplate = async () => {
    setLoading(true)
    try {
      const url = isCreating
        ? '/admin/email_templates'
        : `/admin/email_templates/${selectedTemplate.id}`

      const method = isCreating ? 'POST' : 'PATCH'
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify({
          email_template: {
            email_type: emailTypeKey,
            name: templateName,
            html_content: htmlContent,
            description: description
          }
        })
      })

      if (response.ok) {
        setIsDirty(false)
        loadTemplates()
        setIsCreating(false)
        alert('Template saved successfully!')
      } else {
        const error = await response.json()
        alert(`Error: ${error.errors?.join(', ') || 'Failed to save template'}`)
      }
    } catch (error) {
      console.error('Error saving template:', error)
      alert('Failed to save template')
    } finally {
      setLoading(false)
    }
  }

  const setAsDefault = async (templateId) => {
    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(`/admin/email_templates/${templateId}/set_default`, {
        method: 'PATCH',
        headers: { 'X-CSRF-Token': csrfToken }
      })
      if (response.ok) {
        loadTemplates()
        alert('Default template updated!')
      }
    } catch (error) {
      console.error('Error setting default:', error)
      alert('Failed to set default template')
    }
  }

  const deleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return

    try {
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      const response = await fetch(`/admin/email_templates/${templateId}`, {
        method: 'DELETE',
        headers: { 'X-CSRF-Token': csrfToken }
      })
      if (response.ok) {
        loadTemplates()
        if (selectedTemplate?.id === templateId) {
          setSelectedTemplate(null)
          setIsCreating(false)
        }
      } else {
        const error = await response.json()
        alert(`Error: ${error.errors?.join(', ') || 'Failed to delete template'}`)
      }
    } catch (error) {
      console.error('Error deleting template:', error)
      alert('Failed to delete template')
    }
  }

  const handleInsertTag = (tag) => {
    const textarea = textareaRef.current
    if (!textarea) return

    const start = textarea.selectionStart
    const end = textarea.selectionEnd
    const before = htmlContent.substring(0, start)
    const after = htmlContent.substring(end)

    const newContent = before + tag + after
    setHtmlContent(newContent)
    setIsDirty(true)

    setTimeout(() => {
      textarea.focus()
      const newPosition = start + tag.length
      textarea.setSelectionRange(newPosition, newPosition)
    }, 0)
  }

  const handleBack = (e) => {
    if (isDirty) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        e.preventDefault()
        return
      }
    }
    navigate('/admin/automated_emails')
  }

  if (pageLoading) {
    return (
      <div className="email-templates-page">
        <div className="page-container">
          <div className="loading-state">
            <i className="fa fa-spinner fa-spin fa-2x"></i>
            <p>Loading designer...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="email-designer-page">
      {/* Header with breadcrumbs */}
      <div className="designer-header">
        <nav className="breadcrumbs">
          <Link to="/admin/automated_emails" className="breadcrumb-link" onClick={handleBack}>
            Automated Emails
          </Link>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">
            {emailTypeLabel || emailTypeKey}
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-current">Templates</span>
        </nav>

        <div className="designer-header-controls">
          <button
            className={`btn btn-sm ${showTagPicker ? 'btn-primary' : 'btn-outline-secondary'} mr-2`}
            onClick={() => setShowTagPicker(!showTagPicker)}
            title={showTagPicker ? 'Hide merge tags' : 'Show merge tags'}
          >
            <i className="fa fa-tags mr-1"></i>
            Tags
          </button>
          <button
            className={`btn btn-sm ${showPreview ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setShowPreview(!showPreview)}
            title={showPreview ? 'Hide preview' : 'Show preview'}
          >
            <i className="fa fa-eye mr-1"></i>
            Preview
          </button>
        </div>
      </div>

      {/* Main layout */}
      <div className={`designer-layout ${previewExpanded ? 'preview-expanded' : ''}`}>
        {/* Left sidebar: Template list */}
        <div className="template-sidebar">
          <div className="sidebar-header">
            <button className="btn btn-primary btn-block" onClick={createNewTemplate}>
              <i className="fa fa-plus mr-1"></i> New Template
            </button>
          </div>
          <div className="template-list">
            {templates.map(template => (
              <div
                key={template.id}
                className={`template-item ${selectedTemplate?.id === template.id ? 'active' : ''}`}
              >
                <div className="template-item-header" onClick={() => loadTemplate(template.id)}>
                  <strong>{template.name}</strong>
                  {template.is_default && (
                    <span className="badge badge-success ml-2">Active</span>
                  )}
                </div>
                {template.description && (
                  <div className="template-item-description">{template.description}</div>
                )}
                <div className="template-item-actions">
                  <button
                    className="btn btn-sm btn-outline-primary"
                    onClick={(e) => { e.stopPropagation(); loadTemplate(template.id); }}
                  >
                    Edit
                  </button>
                  {!template.is_default && (
                    <>
                      <button
                        className="btn btn-sm btn-outline-success"
                        onClick={(e) => { e.stopPropagation(); setAsDefault(template.id); }}
                      >
                        Set as Active
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={(e) => { e.stopPropagation(); deleteTemplate(template.id); }}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {templates.length === 0 && (
              <div className="text-muted text-center py-3">
                No templates yet. Create one to get started.
              </div>
            )}
          </div>
        </div>

        {/* Main editor area */}
        <div className="template-editor">
          {(selectedTemplate || isCreating) ? (
            <>
              {/* Template info */}
              <div className="editor-header">
                <div className="row">
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Template Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={templateName}
                        onChange={(e) => { setTemplateName(e.target.value); setIsDirty(true) }}
                        placeholder="e.g., Default Template, Holiday 2024"
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="form-group">
                      <label>Description (optional)</label>
                      <input
                        type="text"
                        className="form-control"
                        value={description}
                        onChange={(e) => { setDescription(e.target.value); setIsDirty(true) }}
                        placeholder="Brief description"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Validation status bar */}
              {validationResult && (
                <div className={`validation-bar ${validationResult.valid ? 'valid' : 'invalid'}`}>
                  {validationResult.valid ? (
                    <span>
                      <i className="fa fa-check-circle mr-1"></i>
                      {validationResult.tags_found?.length || 0} merge tags found, all valid
                    </span>
                  ) : (
                    <span>
                      <i className="fa fa-exclamation-triangle mr-1"></i>
                      {validationResult.errors?.length || 0} issues found
                      {validationResult.errors?.length > 0 && (
                        <span className="ml-2 text-danger">
                          {validationResult.errors.slice(0, 2).join('; ')}
                          {validationResult.errors.length > 2 && '...'}
                        </span>
                      )}
                    </span>
                  )}
                </div>
              )}

              {/* Editor + Tag Picker row */}
              <div className="editor-content-row">
                {/* HTML Editor */}
                <div className="html-editor-wrapper">
                  <div className="editor-toolbar">
                    <span className="toolbar-label">HTML Content</span>
                  </div>
                  <textarea
                    ref={textareaRef}
                    className="form-control html-editor"
                    value={htmlContent}
                    onChange={(e) => { setHtmlContent(e.target.value); setIsDirty(true) }}
                    placeholder="Paste your HTML here..."
                  />
                </div>

                {/* Merge Tag Picker */}
                {showTagPicker && (
                  <div className="tag-picker-wrapper">
                    <MergeTagPicker
                      tags={mergeTags}
                      groupedTags={groupedTags}
                      filters={filters}
                      onInsertTag={handleInsertTag}
                    />
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="editor-actions">
                <button
                  className="btn btn-success"
                  onClick={saveTemplate}
                  disabled={loading || !templateName || !htmlContent}
                >
                  <i className={`fa ${loading ? 'fa-spinner fa-spin' : 'fa-save'} mr-1`}></i>
                  {loading ? 'Saving...' : 'Save Template'}
                </button>
                {!validationResult?.valid && validationResult?.errors?.length > 0 && (
                  <span className="validation-warning ml-3">
                    <i className="fa fa-exclamation-triangle text-warning mr-1"></i>
                    Template has validation warnings but can still be saved
                  </span>
                )}
                {previewExpanded && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary ml-auto"
                    onClick={() => setPreviewExpanded(false)}
                  >
                    <i className="fa fa-compress mr-1"></i>
                    Exit Expanded View
                  </button>
                )}
              </div>
            </>
          ) : (
            <div className="editor-empty">
              <i className="fa fa-file-code fa-4x text-muted"></i>
              <h4 className="mt-3 text-muted">Select or create a template</h4>
              <p className="text-muted">Choose a template from the list or create a new one to start editing.</p>
              <button className="btn btn-primary" onClick={createNewTemplate}>
                <i className="fa fa-plus mr-1"></i> Create New Template
              </button>
            </div>
          )}
        </div>

        {/* Right sidebar: Preview */}
        {showPreview && (selectedTemplate || isCreating) && (
          <div className="template-preview">
            <TemplatePreviewPane
              htmlContent={htmlContent}
              emailType={emailTypeKey}
              requiredContexts={requiredContexts}
              isExpanded={previewExpanded}
              onToggleExpand={() => setPreviewExpanded(!previewExpanded)}
              onOpenFullscreen={() => setFullscreenOpen(true)}
            />
          </div>
        )}
      </div>

      {/* Fullscreen Preview Modal */}
      <PreviewFullscreenModal
        isOpen={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        htmlContent={htmlContent}
        emailType={emailTypeKey}
        requiredContexts={requiredContexts}
      />
    </div>
  )
}

export default EmailDesignerPage
